import { ProblemDetails, isProblemDetails, getProblemErrorMessage } from './problem';

let activeCSRFToken: string | null = null;

export function setCSRFToken(token: string | null) {
  activeCSRFToken = token;
}

export function getCSRFToken(): string | null {
  return activeCSRFToken;
}

export class ApiError extends Error {
  public problem?: ProblemDetails;
  public status: number;

  constructor(message: string, status: number, problem?: ProblemDetails) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
}

export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(path: string, options?: RequestOptions): Promise<T>;
}

export function createApiClient(basePath: string = ''): ApiClient {
  const cleanBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    let url = `${cleanBase}${cleanPath}`;

    if (options.params) {
      const searchParams = new URLSearchParams();
      for (const [key, val] of Object.entries(options.params)) {
        if (val !== undefined) {
          searchParams.append(key, String(val));
        }
      }
      const qs = searchParams.toString();
      if (qs) {
        url += (url.includes('?') ? '&' : '?') + qs;
      }
    }

    const headers = new Headers(options.headers || {});
    headers.set('Accept', 'application/json, application/problem+json');

    const method = (options.method || 'GET').toUpperCase();
    const isUnsafe = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

    if (isUnsafe && activeCSRFToken && !headers.has('X-CSRF-Token')) {
      headers.set('X-CSRF-Token', activeCSRFToken);
    }

    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
      options.body = JSON.stringify(options.body);
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'same-origin',
    });

    // 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json') || contentType.includes('application/problem+json');

    if (!response.ok) {
      if (isJson) {
        const data = await response.json();
        if (isProblemDetails(data)) {
          throw new ApiError(getProblemErrorMessage(data), response.status, data);
        }
        throw new ApiError(data.message || data.error || response.statusText, response.status);
      }
      const text = await response.text();
      throw new ApiError(text || response.statusText, response.status);
    }

    if (isJson) {
      return (await response.json()) as T;
    }

    return (await response.text()) as unknown as T;
  }

  return {
    get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'POST', body: body as BodyInit }),
    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'PUT', body: body as BodyInit }),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>(path, { ...options, method: 'PATCH', body: body as BodyInit }),
    delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
  };
}

export const coreApi = createApiClient('/api/core/v1');
export const financeApi = createApiClient('/api/finance/v1');
export const peopleApi = createApiClient('/api/people/v1');
export const supportApi = createApiClient('/api/support/v1');
export const marketingApi = createApiClient('/api/marketing/v1');
export const dataApi = createApiClient('/api/data/v1');
export const salesApi = createApiClient('/api/sales/v1');
export const projectsApi = createApiClient('/api/projects/v1');
export const operationsApi = createApiClient('/api/operations/v1');
